import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  UI_FILE_KIND_MAP,
  UiFileItem,
  UiFileKind,
  UiFileRejection,
  UiFileRejectionReason,
  UiStoredFileItem,
} from './file-upload.types';
import { UiLoadingModalComponent } from '../loading-modal/loading-modal.component';

let nextId = 0;

/**
 * Molécula: carga de documentos. Presentacional: no sube archivos ni hace HTTP.
 * Emite `UiFileItem[]` para que la feature decida persistencia.
 */
@Component({
  selector: 'ui-file-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiLoadingModalComponent],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  host: {
    class: 'ui-file-upload-host',
    '[class.ui-file-upload-host--block]': 'block()',
  },
})
export class UiFileUploadComponent {
  readonly label = input('');
  readonly placeholder = input(
    'Arrastra archivos aquí o haz clic para seleccionar',
  );
  readonly browseLabel = input('Examinar');
  readonly required = input(false);
  readonly showOptionalHint = input(true);
  readonly requiredHint = input('Obligatorio');
  readonly optionalHint = input('Opcional');
  readonly disabled = input(false);
  readonly readonly = input(false);
  /** Si es falso, el archivo se puede ver y el botón de eliminar no se muestra. */
  readonly removable = input(true);
  readonly invalid = input(false);
  readonly message = input('');
  readonly showMessage = input(true);
  readonly block = input(false);
  readonly fieldId = input('');

  /**
   * Si es `true`, permite apilar varios archivos y el dropzone permanece
   * visible. Si es `false` (default), solo un archivo: al seleccionarlo se
   * oculta el dropzone y vuelve a mostrarse al eliminarlo.
   */
  readonly multiple = input(false);
  readonly allowAnyFileType = input(false);

  /**
   * Tipos permitidos. Vacío = todos los soportados
   * (pdf, word, excel, powerpoint, csv, xml).
   */
  readonly accept = input<UiFileKind[]>([]);

  /** Límite por archivo, en MB. Default: 20. */
  readonly maxSizeMB = input(20);

  /**
   * Límite total de archivos cuando `multiple` está activo.
   * `undefined` = sin límite.
   */
  readonly maxFiles = input<number | undefined>(undefined);

  /** Abre el archivo en nueva pestaña al hacer clic en Ver. */
  readonly openOnView = input(true);
  /** Si es false, oculta la zona para agregar y conserva la lista visible. */
  readonly allowAdd = input(true);
  readonly storedFiles = input<readonly UiStoredFileItem[]>([]);
  /** Muestra la papelera en los archivos ya guardados y solo emite el clic. */
  readonly storedRemovable = input(false);
  readonly storedRemove = output<UiStoredFileItem>();

  readonly value = model<UiFileItem[]>([]);

  readonly valueChange = output<UiFileItem[]>();
  readonly rejected = output<UiFileRejection[]>();
  readonly view = output<UiFileItem>();
  readonly remove = output<UiFileItem>();

  readonly isDragOver = signal(false);
  protected readonly removeConfirmOpen = signal(false);
  private pendingRemove: {
    item: UiFileItem | null;
    stored: UiStoredFileItem | null;
  } | null = null;

  private readonly generatedId = `ui-file-upload-${++nextId}`;

  readonly resolvedId = computed(
    () => this.fieldId() || this.generatedId,
  );

  readonly showLabel = computed(() => this.label().length > 0);

  /**
   * Dropzone visible en modo múltiple siempre; en modo simple solo si
   * aún no hay archivo seleccionado.
   */
  readonly showDropzone = computed(
    () =>
      this.allowAdd() &&
      !this.readonly() &&
      (this.multiple() || this.value().length === 0),
  );

  protected readonly listedFiles = computed(() => [
    ...this.storedFiles().map(file => ({
      id: `stored:${file.id}`,
      name: file.name,
      size: file.size ?? 0,
      extension: fileExtension(file.name),
      url: file.url ?? '',
      removable: this.storedRemovable(),
      item: null as UiFileItem | null,
      stored: file,
    })),
    ...this.value().map(item => ({
      id: item.id,
      name: item.name,
      size: item.size,
      extension: item.extension,
      url: item.url,
      removable: this.removable(),
      item,
      stored: null as UiStoredFileItem | null,
    })),
  ]);

  readonly showRequirement = computed(
    () => this.required() || this.showOptionalHint(),
  );

  readonly requirementText = computed(() =>
    this.required() ? this.requiredHint() : this.optionalHint(),
  );

  readonly activeKinds = computed<UiFileKind[]>(() => {
    const kinds = this.accept();
    if (kinds.length === 0) {
      return Object.keys(UI_FILE_KIND_MAP) as UiFileKind[];
    }
    return kinds;
  });

  readonly nativeAccept = computed(() => {
    if (this.allowAnyFileType()) return '';

    const tokens: string[] = [];
    for (const kind of this.activeKinds()) {
      const spec = UI_FILE_KIND_MAP[kind];
      for (const ext of spec.extensions) tokens.push(`.${ext}`);
      for (const mime of spec.mimeTypes) tokens.push(mime);
    }
    return Array.from(new Set(tokens)).join(',');
  });

  readonly acceptSummary = computed(() => {
    const sizeLabel = `máx. ${this.maxSizeMB()} MB`;
    if (this.allowAnyFileType()) {
      return `Cualquier archivo — ${sizeLabel}`;
    }

    const exts = this.activeKinds()
      .flatMap(k => UI_FILE_KIND_MAP[k].extensions)
      .map(e => e.toUpperCase());
    return `${Array.from(new Set(exts)).join(', ')} — ${sizeLabel}`;
  });

  readonly isInteractive = computed(
    () => !this.disabled() && !this.readonly(),
  );

  readonly fileInputRef =
    viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => {
      for (const item of this.value()) {
        if (item.url) URL.revokeObjectURL(item.url);
      }
    });
  }

  openPicker(): void {
    if (!this.isInteractive()) return;
    this.fileInputRef()?.nativeElement.click();
  }

  onFileInput(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const files = inputEl.files;
    if (files && files.length > 0) {
      this.addFiles(Array.from(files));
    }
    inputEl.value = '';
  }

  onDragOver(event: DragEvent): void {
    if (!this.isInteractive()) return;
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent): void {
    if (!this.isInteractive()) return;
    event.preventDefault();
    this.isDragOver.set(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(Array.from(files));
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.isInteractive()) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openPicker();
    }
  }

  onView(item: UiFileItem, event: MouseEvent): void {
    event.stopPropagation();
    this.view.emit(item);
    this.openFile(item.url);
  }

  onListedView(
    file: { url: string; item: UiFileItem | null },
    event: MouseEvent,
  ): void {
    if (file.item) {
      this.onView(file.item, event);
      return;
    }
    event.stopPropagation();
    this.openFile(file.url);
  }

  onListedRemove(
    file: { item: UiFileItem | null; stored: UiStoredFileItem | null },
    event: MouseEvent,
  ): void {
    event.stopPropagation();
    if (file.item) {
      if (!this.isInteractive() || !this.removable()) return;
      this.pendingRemove = { item: file.item, stored: null };
      this.removeConfirmOpen.set(true);
      return;
    }
    if (!file.stored || !this.storedRemovable() || !this.isInteractive()) return;
    this.pendingRemove = { item: null, stored: file.stored };
    this.removeConfirmOpen.set(true);
  }

  protected onRemoveConfirm(): void {
    const pending = this.pendingRemove;
    this.closeRemoveConfirm();
    if (!pending) return;
    if (pending.item) {
      this.onRemove(pending.item, new MouseEvent('click'));
      return;
    }
    if (pending.stored) this.storedRemove.emit(pending.stored);
  }

  protected onRemoveCancel(): void {
    this.closeRemoveConfirm();
  }

  private closeRemoveConfirm(): void {
    this.removeConfirmOpen.set(false);
    this.pendingRemove = null;
  }

  onRemove(item: UiFileItem, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isInteractive() || !this.removable()) return;
    const next = this.value().filter(f => f.id !== item.id);
    URL.revokeObjectURL(item.url);
    this.value.set(next);
    this.valueChange.emit(next);
    this.remove.emit(item);
  }

  formatSize(bytes: number): string {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let n = bytes;
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    const decimals = n < 10 && i > 0 ? 1 : 0;
    return `${n.toFixed(decimals)} ${units[i]}`;
  }

  iconFor(ext: string): string {
    const e = ext.toLowerCase();
    if (e === 'pdf') return 'icons/ic_doc_pdf.svg';
    if (e === 'doc' || e === 'docx') return 'icons/ic_doc_doc.svg';
    if (e === 'xls' || e === 'xlsx') return 'icons/ic_doc_xls.svg';
    if (e === 'ppt' || e === 'pptx') return 'icons/ic_doc_ppt.svg';
    if (e === 'xml') return 'icons/ic_doc_xml.svg';
    if (e === 'jpg' || e === 'jpeg') return 'icons/ic_doc_jpg.svg';
    if (e === 'png') return 'icons/ic_doc_png.svg';
    if (e === 'svg') return 'icons/ic_doc_svg.svg';
    if (e === 'txt') return 'icons/ic_doc_txt-file.svg';
    return 'icons/ic_doc_file.svg';
  }

  private addFiles(incoming: File[]): void {
    const rejections: UiFileRejection[] = [];
    const accepted: UiFileItem[] = [];
    const allowedExts = new Set(
      this.activeKinds().flatMap(k => UI_FILE_KIND_MAP[k].extensions),
    );
    const allowedMimes = new Set(
      this.activeKinds().flatMap(k => UI_FILE_KIND_MAP[k].mimeTypes),
    );
    const maxBytes = this.maxSizeMB() * 1024 * 1024;

    for (const file of incoming) {
      const ext = this.extractExtension(file.name);
      const typeOk =
        this.allowAnyFileType() ||
        (file.type && allowedMimes.has(file.type)) ||
        allowedExts.has(ext);
      if (!typeOk) {
        rejections.push(this.makeRejection(file, 'type-not-allowed'));
        continue;
      }
      if (file.size > maxBytes) {
        rejections.push(this.makeRejection(file, 'too-large'));
        continue;
      }
      accepted.push(this.toItem(file, ext));
    }

    let nextValue: UiFileItem[];
    if (!this.multiple()) {
      for (const prev of this.value()) URL.revokeObjectURL(prev.url);
      nextValue = accepted.slice(-1);
      for (const extra of accepted.slice(0, -1)) {
        URL.revokeObjectURL(extra.url);
      }
    } else {
      const merged = [...this.value(), ...accepted];
      const max = this.maxFiles();
      if (max !== undefined && merged.length > max) {
        const overflow = merged.length - max;
        const dropped = merged.slice(-overflow);
        for (const d of dropped) {
          URL.revokeObjectURL(d.url);
          rejections.push({
            name: d.name,
            size: d.size,
            reason: 'max-files',
          });
        }
        nextValue = merged.slice(0, max);
      } else {
        nextValue = merged;
      }
    }

    this.value.set(nextValue);
    this.valueChange.emit(nextValue);
    if (rejections.length > 0) {
      this.rejected.emit(rejections);
    }
  }

  private openFile(url: string): void {
    if (!this.openOnView() || !url) return;
    window.open(url, '_blank', 'noopener');
  }

  private toItem(file: File, ext: string): UiFileItem {
    return {
      id: `ui-file-${++nextId}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      extension: ext,
      url: URL.createObjectURL(file),
    };
  }

  private extractExtension(name: string): string {
    return fileExtension(name);
  }

  private makeRejection(
    file: File,
    reason: UiFileRejectionReason,
  ): UiFileRejection {
    return { name: file.name, size: file.size, reason };
  }
}

function fileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx + 1).toLowerCase() : '';
}
